import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShareModal = ({ isOpen, onClose, property, shareUrl, onUnshare }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const isPublished = property?.is_shared && property?.share_token;

  const handleCopyLink = async () => {
    const textToCopy = fullShareText;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const input = document.createElement('input');
      input.value = textToCopy;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareTitle = `Analyse immobilière: ${property?.address || 'Propriété'}`;
  const defaultMessage = `Voyez les résultats de cette analyse immobilière sur ImmoIQ :`;
  const fullShareText = `${defaultMessage}\n${shareUrl}`;
  const hasValidUrl = shareUrl && typeof shareUrl === 'string' && shareUrl.startsWith('http');

  const shareOptions = [
    {
      name: 'Facebook',
      icon: 'Facebook',
      color: '#1877F2',
      url: hasValidUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` : '#'
    },
    {
      name: 'Messenger',
      icon: 'MessageCircle',
      color: '#0084FF',
      url: hasValidUrl ? `fb-messenger://share/?link=${encodeURIComponent(shareUrl)}` : '#',
      fallbackUrl: 'https://www.messenger.com/'
    },
    {
      name: 'WhatsApp',
      icon: 'MessageCircle',
      color: '#25D366',
      url: hasValidUrl ? `https://wa.me/?text=${encodeURIComponent(fullShareText)}` : '#'
    },
    {
      name: 'SMS',
      icon: 'Smartphone',
      color: '#34C759',
      url: hasValidUrl ? `sms:?&body=${encodeURIComponent(fullShareText)}` : '#'
    },
    {
      name: 'X (Twitter)',
      icon: 'Twitter',
      color: '#000000',
      url: hasValidUrl ? `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(defaultMessage)}` : '#'
    },
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: '#0A66C2',
      url: hasValidUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` : '#'
    },
    {
      name: 'Telegram',
      icon: 'Send',
      color: '#0088cc',
      url: hasValidUrl ? `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(defaultMessage)}` : '#'
    },
    {
      name: 'Email',
      icon: 'Mail',
      color: '#EA4335',
      url: hasValidUrl ? `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(defaultMessage + '\n\n' + shareUrl)}` : '#'
    }
  ];

  const handleShareClick = (option) => {
    if (!hasValidUrl) return;
    if (option.url === '#') return;
    if (option.name === 'Email') {
      window.location.href = option.url;
      return;
    }
    if (option.name === 'SMS') {
      window.location.href = option.url;
      return;
    }
    if (option.name === 'Messenger') {
      const w = window.open(option.url, '_blank');
      if (!w || w.closed) {
        navigator.clipboard?.writeText(fullShareText);
        window.open(option.fallbackUrl || option.url, '_blank');
      }
      return;
    }
    window.open(option.url, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full"
        style={{ maxHeight: '90vh', overflow: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Partager l'analyse</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Property Preview */}
          <div className="bg-muted rounded-lg p-3 flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--color-primary)' }}
            >
              <Icon name="Building2" size={24} color="white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">{property?.address}</p>
              <p className="text-sm text-muted-foreground">{property?.city}</p>
            </div>
          </div>

          {/* Copy Link */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Lien de partage (page visiteur en lecture seule)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl || ''}
                readOnly
                placeholder={hasValidUrl ? '' : 'Lien disponible après publication'}
                className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground"
              />
              <Button
                variant={copied ? 'success' : 'primary'}
                iconName={copied ? 'Check' : 'Copy'}
                onClick={handleCopyLink}
              >
                {copied ? 'Copié!' : 'Copier'}
              </Button>
            </div>
          </div>

          {/* Share Options */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Partager via
            </label>
            <div className="grid grid-cols-3 gap-3">
              {shareOptions.map((option) => (
                <button
                  key={option.name}
                  onClick={() => handleShareClick(option)}
                  disabled={!hasValidUrl}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border border-border transition-all ${hasValidUrl ? 'hover:bg-muted cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `${option.color}15` }}
                  >
                    <Icon name={option.icon} size={20} style={{ color: option.color }} />
                  </div>
                  <span className="text-xs text-muted-foreground">{option.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Native Share (Mobile) */}
          {navigator.share && (
            <Button
              variant="outline"
              fullWidth
              iconName="Share2"
              iconPosition="left"
              onClick={() => {
                navigator.share({
                  title: shareTitle,
                  text: defaultMessage,
                  url: shareUrl
                }).catch(() => { });
              }}
            >
              Plus d'options de partage
            </Button>
          )}
        </div>

        {/* Status & Footer */}
        <div className="p-4 border-t border-border bg-muted/50 space-y-3">
          {/* Published Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: isPublished ? '#22c55e' : '#9ca3af' }}
              />
              <span className="text-sm font-medium text-foreground">
                {isPublished ? 'Publié' : 'Non publié'}
              </span>
            </div>
            {isPublished && onUnshare && (
              <button
                onClick={onUnshare}
                className="text-sm text-destructive hover:underline"
              >
                Retirer le partage
              </button>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Les visiteurs pourront voir votre analyse en mode lecture seule
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
